import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Activity, ChevronRight, FolderKanban } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { StatusPill, type StatusTone } from '@vnetwork/ui/data';
import { Button, Skeleton, Badge } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useMyProjects, type Project } from '@/api/projects';
import { GetStartedGuide } from './onboarding';

const BASE = '/services/healthcheck';

function statusTone(status: Project['status']): StatusTone {
  if (status === 'active') return 'success';
  if (status === 'suspended') return 'warning';
  return 'muted';
}

/**
 * Picks the project whose monitors to manage. Watchpoints are project-scoped, so
 * this is the entry to the console. With a single project it jumps straight in.
 */
export function HealthcheckProjectPicker() {
  const navigate = useNavigate();
  const { data: projects = [], isLoading, isError, refetch } = useMyProjects();

  useEffect(() => {
    if (!isLoading && !isError && projects.length === 1) {
      navigate(`${BASE}/${projects[0].id}`, { replace: true });
    }
  }, [isLoading, isError, projects, navigate]);

  return (
    <Container size="xl">
      <PageHeader
        icon={<Activity size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to="/services">Services</Link></BreadcrumbItem>
            <BreadcrumbItem current>Healthcheck &amp; Alerting</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            Healthcheck &amp; Alerting
            <Badge tone="info" appearance="subtle" size="sm">
              Beta
            </Badge>
          </span>
        }
        description="Uptime monitors, latency tracking and incident alerting for your endpoints and services."
      />

      {/* Workspace is guaranteed by WorkspaceGate; a 403 here is a real error. */}
      {isError ? (
        <Section title="Choose a project">
          <Alert
            tone="danger"
            title="Couldn't load projects"
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            The project list is temporarily unavailable. Please try again.
          </Alert>
        </Section>
      ) : isLoading ? (
        <Section title="Choose a project">
          <div style={grid}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 116 }} />
            ))}
          </div>
        </Section>
      ) : projects.length === 0 ? (
        /* State B — active workspace, no projects: guided get-started experience. */
        <Section title="Get started" description="A quick tour of how monitoring works — create your first project to begin.">
          <GetStartedGuide />
        </Section>
      ) : (
        <Section title="Choose a project" description="Monitors are scoped to a project. Pick one to manage its watchpoints and incidents.">
          <div style={grid}>
            {projects.map((p) => (
              <button key={p.id} type="button" style={card} onClick={() => navigate(`${BASE}/${p.id}`)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', marginBottom: 'var(--vn-space-3)' }}>
                  <span style={iconWrap}>
                    <FolderKanban size={20} />
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {p.name}
                    </div>
                    <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                      {p.slug}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
                  <StatusPill tone={statusTone(p.status)}>{p.status}</StatusPill>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                    Open <ChevronRight size={14} />
                  </span>
                </div>
              </button>
            ))}
          </div>
        </Section>
      )}
    </Container>
  );
}

const grid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 'var(--vn-space-4)',
};
const card: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  font: 'inherit',
  color: 'var(--vn-fg-default)',
  border: '1px solid var(--vn-border-default)',
  borderRadius: 'var(--vn-radius-lg)',
  background: 'var(--vn-bg-surface)',
  padding: 'var(--vn-space-4)',
};
const iconWrap: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: 'var(--vn-radius-md)',
  background: 'var(--vn-bg-muted)',
  color: 'var(--vn-fg-default)',
  flexShrink: 0,
};
